import type { ServerStatus } from '@pixelworld/shared';
import { useEffect, useState } from 'react';
import { Button } from '../../components/Button';
import { da } from '../../i18n/da';
import { fetchServerStatus } from '../auth/auth-api';
import styles from './ChatLogPanel.module.css';

const time = new Intl.DateTimeFormat('da-DK', {
  day: 'numeric',
  month: 'short',
  hour: '2-digit',
  minute: '2-digit',
});

/**
 * For the admin (M11): who plays right now, new reports and what went wrong this week, so
 * problems are seen before the children tell about them.
 */
export function StatusPanel() {
  const [status, setStatus] = useState<ServerStatus | null>(null);
  const [failed, setFailed] = useState(false);
  // Bumped by the refresh button to load the overview again.
  const [version, setVersion] = useState(0);

  useEffect(() => {
    let active = true;
    const load = async () => {
      const result = await fetchServerStatus();
      if (!active) return;
      setFailed(!result.ok);
      if (result.ok) setStatus(result.data);
    };
    void load();
    return () => {
      active = false;
    };
  }, [version]);

  return (
    <section className={styles.panel}>
      <div className={styles.header}>
        <strong>{da.admin.statusTitle}</strong>
        <Button size="small" variant="quiet" onClick={() => setVersion((value) => value + 1)}>
          {da.admin.chatRefresh}
        </Button>
      </div>
      {failed && <p className={styles.note}>{da.admin.statusLoadFailed}</p>}
      {status && (
        <>
          <p className={styles.note} data-testid="server-status">
            {da.admin.statusLine(status.playersOnline, status.rooms)} ·{' '}
            {da.admin.statusStarted(time.format(status.startedAt))}
          </p>
          {status.unseenReports > 0 && (
            <p className={styles.stopped}>{da.admin.statusReports(status.unseenReports)}</p>
          )}
          {status.errors.length === 0 ? (
            <p className={styles.note}>{da.admin.statusNoErrors}</p>
          ) : (
            <ul className={styles.list} aria-label={da.admin.statusErrors}>
              {status.errors.map((error) => (
                <li key={`${error.source}-${error.day}-${error.message}`}>
                  <span className={styles.meta}>
                    {time.format(error.lastAt)} · {da.admin.errorSource[error.source]} ·{' '}
                    {da.admin.errorCount(error.count)}
                  </span>
                  <span title={error.detail ?? undefined}>{error.message}</span>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </section>
  );
}
